import {Elastic, Sine, TimelineMax} from "gsap";
import {Anchor} from "../core/Anchor";
import {Ease} from "../core/Animatable";
import {BaseButton, ButtonEventListener, HoverableButton, TextureButton} from "../core/Button";
import {IPoint} from "../core/Geom";
import {Mix} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import {Assets} from "../data/Assets";
import {voOptionsButtons} from "../data/VoiceOver";
import {AudioChannel} from "../managers/AudioChannel";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {Focusable} from "./Focusable";
import {TransformBackup} from "./TransformBackup";

export class Button extends Mix(TextureButton, HoverableButton(), Focusable) {

    public clickSfx = Assets.audio.uiClick.assetName;
    public hoverVO?: string;

    constructor(texture?: PIXI.Texture | string, onClick?: ButtonEventListener<Button>, anchor?: IPoint) {
        super(texture, onClick, anchor);

        this.onOver.add(() => {
            if (this.hoverVO) {
                VoiceOverManager.instance.playVO(this.hoverVO, voOptionsButtons);
            }
        });
        this.onDown.add(() => {
            if (this.clickSfx) {
                AudioChannel.SFX.play(this.clickSfx);
            }
        });
    }
}

export const PulsatingButton = {
    // Do not use this argument here because it's not compiled properly with arguments having default values
    pulse(scale: number = 1.15, interval: number = 1.5, startImmediately: boolean = false) {
        const b = this as any as BaseButton;
        (this as any).stopPulse();
        const initScale = b.scale.x;
        const loop = new TimelineMax({repeat: -1})
            .to(this, 0.15, {
                scaleX: initScale * scale,
                scaleY: initScale * scale,
                ease: Sine.easeInOut,
            }, interval)
            .to(this, 1.0, {
                scaleX: initScale,
                scaleY: initScale,
                ease: Elastic.easeOut,
            })
            .call(() => {
                loop.seek(0);
                if (b.isOver) {
                    loop.pause();
                }
                if ((b as any)._pulsatingButtonStopPulse) {
                    PulsatingButton.stopPulse.call(b, true);
                }
            });
        if (startImmediately) {
            loop.seek(interval);
        }
        const onOut = () => {
            loop.resume();
        };
        b.onOut.add(onOut);
        const destroy = b.destroy;
        b.destroy = () => {
            loop.kill();
            destroy.call(b);
        };
        (this as any)._pulsatingButtonOriginalDestroy = destroy;
        (this as any)._pulsatingButtonLoop = loop;
        (this as any)._pulsatingButtonOnOut = onOut;
        (this as any)._pulsatingButtonInitScale = initScale;
        (this as any)._pulsatingButtonStopPulse = false;
    },
    stopPulse(immediately = true) {
        if ((this as any)._pulsatingButtonLoop) {
            if (immediately) {
                const b = this as any as BaseButton;
                b.destroy = (this as any)._pulsatingButtonOriginalDestroy;
                b.onOut.remove((this as any)._pulsatingButtonOnOut);
                (this as any)._pulsatingButtonLoop.kill();
                (this as any)._pulsatingButtonLoop = undefined;
                b.scale.set((this as any)._pulsatingButtonInitScale);
            } else {
                (this as any)._pulsatingButtonStopPulse = true;
            }
        }
    },
};

export class ButtonPulse extends Mix(Button, PulsatingButton) {
}

export class ButtonHome extends Mix(ButtonPulse, TransformBackup) {
    public clickSfx = Assets.audio.uiSelect.assetName;
}

export class ButtonLoading extends ButtonPulse {

    private loader?: Sprite;

    public updateState() {
        super.updateState();

        if (!this.enabled && !this.loader) {
            this.loader = this.visuals.addChild(new Sprite("loader", Anchor.middleCenter));
            this.loader.width = this.sprite.width * 0.8;
            this.loader.y = -this.sprite.height * 0.03;
            this.loader.height = this.loader.width;
            this.loader.tweenTo(0.8, Ease.none, {
                rotation: -Math.PI * 2,
                repeat: -1,
            });
        }
        if (this.loader) {
            this.loader.visible = !this.enabled;
        }
    }
}
